import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { z } from "zod";
import { Section } from "@/components/Section";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";

export const Route = createFileRoute("/entrar")({
  ssr: false,
  validateSearch: z.object({
    next: z.string().optional(),
  }),
  head: () => ({
    meta: [
      { title: "Entrar · Academia SVG" },
      { name: "description", content: "Acesse sua área de aluno da Academia SVG." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: EntrarAluno,
});

function EntrarAluno() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/entrar" }) as { next?: string };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) navigate({ to: (search.next as any) || "/aluno", replace: true });
    });
  }, [navigate, search.next]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      navigate({ to: (search.next as any) || "/aluno", replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao entrar");
    } finally {
      setLoading(false);
    }
  }

  async function onGoogle() {
    setError(null);
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin + "/entrar",
    });
    if (result.error) setError(result.error.message ?? "Falha ao entrar com Google");
  }

  return (
    <Section>
      <div className="mx-auto max-w-md">
        <div className="card-glass">
          <div className="text-xs uppercase tracking-wider text-primary">Academia SVG</div>
          <h1 className="mt-2 text-2xl font-bold">Entrar na sua conta</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Acesse suas aulas, tarefas e ofertas exclusivas.
          </p>

          <button
            type="button"
            onClick={onGoogle}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-background/60 px-4 py-3 text-sm font-medium hover:bg-secondary"
          >
            <GoogleIcon /> Entrar com Google
          </button>

          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" /> ou <span className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={onSubmit} className="space-y-3">
            <div>
              <label htmlFor="email" className="text-sm font-medium">E-mail</label>
              <input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full rounded-lg border border-border bg-background/60 px-4 py-3 text-sm focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label htmlFor="password" className="text-sm font-medium">Senha</label>
              <input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full rounded-lg border border-border bg-background/60 px-4 py-3 text-sm focus:border-primary focus:outline-none" />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <button type="submit" disabled={loading} className="btn-primary w-full">
              {loading ? "Aguarde…" : "Entrar"}
            </button>
          </form>

          <div className="mt-6 space-y-2 text-center text-xs">
            <p className="text-muted-foreground">
              Ainda não tem conta?{" "}
              <Link to="/criar-conta" className="font-semibold text-primary hover:underline">
                Criar conta de aluno
              </Link>
            </p>
            <p className="text-muted-foreground">
              Esqueceu a senha ou precisa de ajuda?{" "}
              <a
                href="https://wa.me/555596852045?text=Ol%C3%A1%21%20Sou%20aluno%20da%20Academia%20SVG%20e%20preciso%20resetar%20minha%20senha%20ou%20tirar%20uma%20d%C3%BAvida."
                target="_blank"
                rel="noopener"
                className="font-semibold text-primary hover:underline"
              >
                Falar com o suporte no WhatsApp
              </a>
            </p>
            <p className="text-muted-foreground">
              É administrador?{" "}
              <Link to="/auth" className="hover:text-foreground">Acessar painel administrativo</Link>
            </p>
          </div>
        </div>
      </div>
    </Section>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}